import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLink, FiCreditCard, FiSmartphone, FiTrendingDown } = FiIcons;

const steps = [
  {
    icon: FiLink,
    title: "Link Your Account",
    description: "Enter your current carrier and device balance. We'll verify the payoff amount in seconds with no hard credit pull.",
    bg: "bg-blue-50",
    iconColor: "text-blue-600",
    graphic: "📱",
  },
  {
    icon: FiCreditCard,
    title: "Choose Your Path",
    description: "Keep your phone with a flexible financing plan or trade it in to wipe the debt instantly. We pay your carrier directly.",
    bg: "bg-indigo-50",
    iconColor: "text-indigo-600",
    graphic: "💳",
  },
  {
    icon: FiSmartphone,
    title: "Switch Your Plan",
    description: "Browse curated MVNO plans starting as low as $8/mo. We'll help you pick the best network for your area.",
    bg: "bg-purple-50",
    iconColor: "text-purple-600",
    graphic: "📶",
  },
  {
    icon: FiTrendingDown,
    title: "Start Saving",
    description: "Activate your new plan and use your massive monthly savings to comfortably handle your new, lower payment.",
    bg: "bg-green-50",
    iconColor: "text-green-600",
    graphic: "💰",
  },
];

export default function StepsSection() {
  return (
    <div className="py-24 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-extrabold text-slate-900 mb-4 tracking-tight">The Modern Way to Switch</h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">We've simplified the carrier buyout process into four transparent steps.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="relative bg-slate-50 rounded-3xl p-8 flex flex-col items-start hover:bg-white hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 border border-transparent hover:border-slate-100 group"
            >
              <div className={`w-16 h-16 rounded-2xl ${step.bg} flex items-center justify-center text-3xl mb-6 group-hover:scale-110 transition-transform`}>
                {step.graphic}
              </div>
              <div className="flex items-center gap-2 mb-3">
                <span className={`text-xs font-bold uppercase tracking-widest ${step.iconColor}`}>Step {idx + 1}</span>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
              <p className="text-slate-500 leading-relaxed text-sm">{step.description}</p>
              
              {/* Decorative number */}
              <div className="absolute top-6 right-8 text-5xl font-black text-slate-200/50 select-none group-hover:text-blue-100 transition-colors">
                {idx + 1}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Dynamic Savings Calculator Teaser */}
        <div className="mt-20 relative rounded-3xl overflow-hidden bg-slate-900 p-8 md:p-12">
          <div className="absolute top-0 right-0 w-1/2 h-full opacity-10 pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-l from-blue-500 via-transparent to-transparent"></div>
          </div>
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-lg text-center md:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">Ready to see your savings?</h3>
              <p className="text-slate-400">Most users save over $1,200 in their first year after switching from a major carrier.</p>
            </div>
            <div className="flex flex-wrap justify-center gap-8">
              <div className="text-center">
                <div className="text-3xl font-black text-blue-400">$1,440</div>
                <div className="text-slate-500 text-xs uppercase tracking-widest mt-1">Avg. Annual Savings</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-black text-indigo-400">&lt; 3 min</div>
                <div className="text-slate-500 text-xs uppercase tracking-widest mt-1">Setup Time</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}